import {LoadWrapper} from 'components/LoadWrapper';
import {ViewAuthorizer} from 'components/ViewAuthorizer';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {PermissionGroup, UserRole} from 'types';
import {
	PermissionTooltipTranslationProvider,
	ProviderThatEnablesGettingTooltipsFromContext,
} from 'features/localizedTooltips';
import {EntityPage} from 'components';
import {renderArrayField} from 'components/EntityList/ColumnRenderers';
import {IColumn} from '@fluentui/react/lib/components/DetailsList';
import {Control} from 'react-hook-form';
import {ControlledPeoplePicker} from 'components/hookForms/ControlledPeoplePicker';
import {ControlledTagPicker, ControlledTextField} from 'components/hookForms';
import {useGetAllKeywordsQuery} from './hooks/keywords.generated';
import {useGetAllMarketsQuery} from './hooks/markets.generated';
import {
	GetAllPermissionGroupsDocument,
	useCreatePermissionGroupMutation,
	useDeletePermissionGroupMutation,
	useGetAllPermissionGroupsQuery,
	useUpdatePermissionGroupMutation,
} from './hooks/permissions.generated';
import {mapToRef} from 'helpers';

type DisplayablePermissionGroup = Omit<
	PermissionGroup,
	'keywords' | 'markets' | 'users'
> & {
	keywordRefs: {
		id: string;
		name: string;
	}[];
	marketRefs: {
		id: string;
		name: string;
	}[];
	userRefs: {
		id: string;
		name: string;
	}[];
};

const AdminPermissionsPage: React.FC = () => {
	const {t} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminPermissionsPage',
	});

	const {data: keywordsData} = useGetAllKeywordsQuery();
	const {data: marketsData} = useGetAllMarketsQuery();
	const {data: permissionGroupData, loading: permissionGroupsLoading} =
		useGetAllPermissionGroupsQuery();

	const [updatePermissionGroupMutation] = useUpdatePermissionGroupMutation();
	const [createPermissionGroupMutation] = useCreatePermissionGroupMutation();
	const [deletePermissionGroupMutation] = useDeletePermissionGroupMutation();
	const refetchQueries = [GetAllPermissionGroupsDocument];

	const [permissionGroups, setPermissionGroups] = React.useState<
		DisplayablePermissionGroup[]
	>([]);

	React.useEffect(() => {
		if ((permissionGroupData?.allPermissionGroups?.length ?? 0) > 0) {
			setPermissionGroups(
				permissionGroupData?.allPermissionGroups?.map(pg => {
					return {
						...pg,
						keywordRefs: pg.keywords,
						marketRefs: pg.markets,
						userRefs: pg.users,
					};
				}) ?? [],
			);
		}
	}, [permissionGroupData]);

	const allKeywords = React.useMemo(
		() => keywordsData?.keywords ?? [],
		[keywordsData],
	);

	const allMarkets = React.useMemo(
		() => marketsData?.markets ?? [],
		[marketsData],
	);

	const columns: IColumn[] = [
		{
			key: 'company',
			name: t('Company'),
			fieldName: 'company',
			minWidth: 150,
			isResizable: true,
		},
		{
			key: 'market',
			name: t('Markets'),
			fieldName: 'marketRefs',
			minWidth: 200,
			isResizable: true,
			onRender: renderArrayField(),
		},
		{
			key: 'keywords',
			name: t('Keywords'),
			fieldName: 'keywordRefs',
			minWidth: 200,
			isResizable: true,
			onRender: renderArrayField(),
		},
		{
			key: 'users',
			name: t('Users'),
			fieldName: 'userRefs',
			minWidth: 200,
			isResizable: true,
			onRender: renderArrayField(),
		},
	];

	const createPermissionGroup = (newEntity: DisplayablePermissionGroup) => {
		createPermissionGroupMutation({
			variables: {
				input: {
					name: newEntity.name,
					company: newEntity.company ?? '',
					keywordRefs: mapToRef(newEntity.keywordRefs),
					marketRefs: mapToRef(newEntity.marketRefs),
					userRefs: mapToRef(newEntity.userRefs),
				},
			},
			refetchQueries,
		});
	};

	const updatePermissionGroup = (updatedEntity: DisplayablePermissionGroup) => {
		updatePermissionGroupMutation({
			variables: {
				input: {
					id: updatedEntity.id,
					name: updatedEntity.name,
					company: updatedEntity.company ?? '',
					keywordRefs: mapToRef(updatedEntity.keywordRefs),
					marketRefs: mapToRef(updatedEntity.marketRefs),
					userRefs: mapToRef(updatedEntity.userRefs),
				},
			},
			refetchQueries,
		});
	};

	const deletePermissionGroup = (entityId: string) => {
		deletePermissionGroupMutation({
			variables: {
				input: {
					id: entityId,
				},
			},
			refetchQueries,
		});
	};

	const getKey = (item: {id: string}) => item.id;
	const getName = (item: {name: string}) => item.name;
	const renderAdditionalFormElements = (
		control: Control<DisplayablePermissionGroup>,
	) => formElements(control);

	const formElements = (control: Control<DisplayablePermissionGroup>) => {
		return (
			<div>
				<ProviderThatEnablesGettingTooltipsFromContext>
					<ControlledTextField
						control={control}
						name={'company'}
						label={t('Company')}
						type='text'
					/>
					<ControlledTagPicker
						name={'marketRefs'}
						label={t('Markets')}
						control={control}
						selectableItems={allMarkets}
						getKey={getKey}
						getName={getName}
					/>
					<ControlledTagPicker
						name={'keywordRefs'}
						label={t('Keywords')}
						control={control}
						selectableItems={allKeywords}
						getKey={getKey}
						getName={getName}
					/>
					<ControlledPeoplePicker
						control={control}
						label={t('Users')}
						name={'userRefs'}
						roles={[UserRole.RegulationReader]}
					/>
				</ProviderThatEnablesGettingTooltipsFromContext>
			</div>
		);
	};

	return (
		<ViewAuthorizer roles={[UserRole.SystemAdministrator]}>
			<LoadWrapper loading={permissionGroupsLoading}>
				<PermissionTooltipTranslationProvider>
					<EntityPage
						items={permissionGroups}
						deleteEntity={deletePermissionGroup}
						entityDisplayName={t('Permissions')}
						createEntity={createPermissionGroup}
						updateEntity={updatePermissionGroup}
						additionalColumns={columns}
						renderAdditionalFormElements={renderAdditionalFormElements}
					/>
				</PermissionTooltipTranslationProvider>
			</LoadWrapper>
		</ViewAuthorizer>
	);
};

export default AdminPermissionsPage;
